import { createContext, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import CheckCircleLinear from "~icons/solar/check-circle-linear";
import CloseCircleLinear from "~icons/solar/close-circle-linear";
import InfoCircleLinear from "~icons/solar/info-circle-linear";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ToastVariant = "success" | "error" | "info";

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  fading: boolean;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
export const ToastContext = createContext<ToastContextValue | null>(null);

// ---------------------------------------------------------------------------
// Config per variant
// ---------------------------------------------------------------------------

const VARIANT_CONFIG: Record<
  ToastVariant,
  {
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    className: string;
    /** ms before the fade-out animation starts */
    dismissDelay: number;
  }
> = {
  success: {
    icon: CheckCircleLinear,
    className: "bg-status-green-text text-white",
    dismissDelay: 4000,
  },
  error: {
    icon: CloseCircleLinear,
    className: "bg-error text-white",
    dismissDelay: 6000,
  },
  info: {
    icon: InfoCircleLinear,
    className: "bg-status-blue-text text-white",
    dismissDelay: 4000,
  },
};

/** Duration of the toast-out CSS animation (must match @keyframes toast-out) */
const FADE_OUT_DURATION = 300;

// ---------------------------------------------------------------------------
// Single toast item
// ---------------------------------------------------------------------------

interface ToastItemProps {
  toast: Toast;
}

function ToastItem({ toast }: ToastItemProps) {
  const config = VARIANT_CONFIG[toast.variant];
  const Icon = config.icon;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        "flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5",
        "text-sm font-semibold tracking-tight shadow-pop",
        config.className,
        toast.fading
          ? "motion-safe:[animation:var(--animate-toast-out)]"
          : "motion-safe:[animation:var(--animate-toast-in)]"
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span>{toast.message}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Map from toast id → timer refs so we can clear them on unmount
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>[]>>(
    new Map()
  );
  const idCounter = useRef(0);

  const dismiss = useCallback((id: string) => {
    // Start fade-out animation
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, fading: true } : t))
    );
    // Remove after animation completes
    const removeTimer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timersRef.current.delete(id);
    }, FADE_OUT_DURATION);

    // Store the remove timer (overwrite the dismiss timer entry)
    const existing = timersRef.current.get(id) ?? [];
    existing.push(removeTimer);
    timersRef.current.set(id, existing);
  }, []);

  const addToast = useCallback(
    (message: string, variant: ToastVariant) => {
      const id = `toast-${++idCounter.current}`;
      const config = VARIANT_CONFIG[variant];

      setToasts((prev) => [{ id, message, variant, fading: false }, ...prev]);

      // Auto-dismiss
      const dismissTimer = setTimeout(() => {
        dismiss(id);
      }, config.dismissDelay);

      timersRef.current.set(id, [dismissTimer]);
    },
    [dismiss]
  );

  const contextValue: ToastContextValue = {
    success: useCallback(
      (message: string) => addToast(message, "success"),
      [addToast]
    ),
    error: useCallback(
      (message: string) => addToast(message, "error"),
      [addToast]
    ),
    info: useCallback(
      (message: string) => addToast(message, "info"),
      [addToast]
    ),
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div
          aria-label="Notifications"
          className="pointer-events-none fixed bottom-8 left-1/2 z-[9999] flex -translate-x-1/2 flex-col items-center gap-2"
        >
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
